"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.INVOICE_CHANGE_FIELD_LABELS = exports.INVOICE_CHANGE_SECTION_LABELS = void 0;
exports.invoiceChangeSectionLabel = invoiceChangeSectionLabel;
exports.invoiceChangeFieldLabel = invoiceChangeFieldLabel;
exports.formatInvoiceChangeDisplayValue = formatInvoiceChangeDisplayValue;
exports.formatInvoiceChangeLogLine = formatInvoiceChangeLogLine;
exports.withInvoiceChangeBullet = withInvoiceChangeBullet;
exports.groupInvoiceChangeLogLinesForClient = groupInvoiceChangeLogLinesForClient;
exports.invoiceChangeLogSessionTitle = invoiceChangeLogSessionTitle;
exports.groupMaterialChangeSessionsForClient = groupMaterialChangeSessionsForClient;
exports.formatWhatChangedSummary = formatWhatChangedSummary;
exports.formatRecurringScheduleSummaryForClient = formatRecurringScheduleSummaryForClient;
exports.INVOICE_CHANGE_SECTION_LABELS = {
    details: "Details",
    line_items: "Line items",
    payment_terms: "Payment terms",
    schedule: "Schedule",
};
/** Human labels for known changelog field keys (including patterns for tranche/lineItem). */
exports.INVOICE_CHANGE_FIELD_LABELS = {
    projectName: "Project name",
    invoiceNumber: "Invoice number",
    invoiceNotes: "Invoice notes",
    termsText: "Payment terms",
    dueDate: "Due date",
    amount: "Amount",
    lineItem: "Line item",
    invoiceTotal: "Total invoice amount",
    paymentTermsUnchanged: "Payment terms",
    recurringFrequency: "Frequency",
    recurringEndCondition: "End condition",
    recurringEndDate: "End date",
    recurringCount: "Invoice count",
    recurringPausedUntil: "Paused until",
    recurringScheduleCancelled: "Schedule cancelled",
    recurringSkippedCycles: "Skipped cycles",
    autoSendRecurringInvoices: "Auto-send invoices",
    "acceptedPaymentMethods.bank": "Bank transfer",
    "acceptedPaymentMethods.card": "Credit / debit card",
    "acceptedPaymentMethods.paynow": "PayNow",
    "acceptedPaymentMethods.swift": "SWIFT transfer",
};
const RECURRING_FREQUENCY_LABELS = {
    weekly: "Weekly",
    monthly: "Monthly",
    quarterly: "Quarterly",
    annually: "Annually",
    yearly: "Annually",
};
const RECURRING_END_CONDITION_LABELS = {
    indefinite: "No end date",
    date: "On a specific date",
    count: "After a set number of invoices",
};
/** Legacy line-item display: `Name (1 × 10)` from older diffs. */
const LEGACY_QTY_UNIT = /^(.+?)\s*\((\d+)\s*[×xX]\s*([\d,]+(?:\.\d+)?)\)$/;
const MONEY_DISPLAY = /^(?:[A-Z]{3}\s+)?[\d,]+(?:\.\d{1,2})?$/;
function invoiceChangeSectionLabel(section) {
    const key = String(section ?? "").trim();
    if (!key)
        return "Details";
    return exports.INVOICE_CHANGE_SECTION_LABELS[key] ?? titleCaseFromKey(key);
}
function invoiceChangeFieldLabel(field) {
    const key = String(field ?? "").trim();
    if (!key)
        return "Field";
    if (exports.INVOICE_CHANGE_FIELD_LABELS[key])
        return exports.INVOICE_CHANGE_FIELD_LABELS[key];
    const trancheDue = key.match(/^tranche\.(\d+)\.dueDate$/);
    if (trancheDue)
        return `Tranche ${trancheDue[1]} due date`;
    const trancheAmount = key.match(/^tranche\.(\d+)\.amount$/);
    if (trancheAmount)
        return `Tranche ${trancheAmount[1]}`;
    const tranche = key.match(/^tranche\.(\d+)$/);
    if (tranche)
        return `Tranche ${tranche[1]}`;
    const lineItemAmount = key.match(/^lineItem\.(\d+)\.amount$/);
    if (lineItemAmount)
        return `Line item ${lineItemAmount[1]}`;
    const lineItemDesc = key.match(/^lineItem\.(\d+)\.description$/);
    if (lineItemDesc)
        return `Line item ${lineItemDesc[1]}`;
    const lineItem = key.match(/^lineItem\.(\d+)$/);
    if (lineItem)
        return `Line item ${lineItem[1]}`;
    return titleCaseFromKey(key);
}
/** Format stored/raw recurring metadata values for client-facing copy. */
function formatInvoiceChangeDisplayValue(field, value, opts) {
    if (value === null || value === undefined || value === "")
        return "—";
    const fieldKey = String(field ?? "");
    const asString = String(value).trim();
    if (fieldKey === "recurringFrequency") {
        return RECURRING_FREQUENCY_LABELS[asString.toLowerCase()] ?? titleCaseFromKey(asString);
    }
    if (fieldKey === "recurringEndCondition") {
        if (asString === "count") {
            const n = Math.floor(Number(opts?.recurringCount) || 0);
            if (n > 0)
                return n === 1 ? "After 1 invoice" : `After ${n} invoices`;
        }
        return RECURRING_END_CONDITION_LABELS[asString] ?? titleCaseFromKey(asString);
    }
    if (fieldKey === "recurringScheduleCancelled" || fieldKey === "autoSendRecurringInvoices") {
        if (value === true || asString === "true")
            return "Yes";
        if (value === false || asString === "false")
            return "No";
    }
    if (fieldKey === "recurringSkippedCycles") {
        return "Updated";
    }
    if (RECURRING_END_CONDITION_LABELS[asString])
        return RECURRING_END_CONDITION_LABELS[asString];
    if (RECURRING_FREQUENCY_LABELS[asString.toLowerCase()]) {
        return RECURRING_FREQUENCY_LABELS[asString.toLowerCase()];
    }
    return asString;
}
/**
 * Client-friendly single line for a changelog entry.
 * HAN-284 examples:
 * - `Renovation: SGD 50,000.00 → SGD 100,000.00`
 * - `Total invoice amount increased from SGD 50,000.00 to SGD 100,000.00 (+SGD 50,000.00)`
 */
function formatInvoiceChangeLogLine(entry) {
    const field = String(entry.field ?? "").trim();
    const changeType = String(entry.changeType ?? "field_change");
    const normalized = normalizeLegacyLineItemPair(entry.oldDisplay, entry.newDisplay);
    const oldDisplay = formatInvoiceChangeDisplayValue(field, normalized.oldDisplay);
    const newDisplay = formatInvoiceChangeDisplayValue(field, normalized.newDisplay);
    const label = String(entry.label ?? normalized.label ?? "").trim() || invoiceChangeFieldLabel(field);
    if (field === "paymentTermsUnchanged") {
        return newDisplay !== "—" ? newDisplay : oldDisplay;
    }
    if (changeType === "item_added") {
        if (looksLikeMoney(newDisplay))
            return `${label}: ${newDisplay}`;
        return `Added: ${newDisplay !== "—" ? newDisplay : label}.`;
    }
    if (changeType === "item_removed") {
        if (looksLikeMoney(oldDisplay))
            return `Removed ${label}: ${oldDisplay}`;
        return `Removed: ${oldDisplay !== "—" ? oldDisplay : label}.`;
    }
    if (field === "invoiceTotal") {
        return formatInvoiceTotalLine(oldDisplay, newDisplay);
    }
    const isRename = field.endsWith(".description") ||
        (normalized.kind === "rename" && !looksLikeMoney(oldDisplay) && !looksLikeMoney(newDisplay));
    if (isRename) {
        if (oldDisplay === newDisplay)
            return `${label} updated.`;
        return `${oldDisplay} → ${newDisplay}`;
    }
    if (looksLikeMoney(oldDisplay) && looksLikeMoney(newDisplay)) {
        return `${label}: ${oldDisplay} → ${newDisplay}`;
    }
    if (oldDisplay === "—" && newDisplay !== "—") {
        return `${label} set to ${newDisplay}.`;
    }
    if (newDisplay === "—" && oldDisplay !== "—") {
        return `${label} cleared (was ${oldDisplay}).`;
    }
    if (oldDisplay === newDisplay) {
        return `${label} updated.`;
    }
    return `${label} changed from ${oldDisplay} to ${newDisplay}.`;
}
/** HAN-325: dash prefix for scannable What changed lists (email + in-app). */
function withInvoiceChangeBullet(line) {
    const trimmed = String(line ?? "").trim();
    if (!trimmed)
        return trimmed;
    if (/^[-•*]\s+/.test(trimmed))
        return trimmed;
    return `- ${trimmed}`;
}
function groupInvoiceChangeLogLinesForClient(entries, materialOnly = true) {
    const grouped = new Map();
    // Stable section order for HAN-284-style summaries
    const sectionOrder = ["line_items", "payment_terms", "schedule", "details"];
    for (const entry of entries) {
        if (materialOnly && entry.isMaterial === false)
            continue;
        const section = String(entry.section ?? "details");
        const lines = grouped.get(section) ?? [];
        lines.push(withInvoiceChangeBullet(formatInvoiceChangeLogLine(entry)));
        grouped.set(section, lines);
    }
    const orderedSections = [
        ...sectionOrder.filter((s) => grouped.has(s)),
        ...[...grouped.keys()].filter((s) => !sectionOrder.includes(s)),
    ];
    return orderedSections.map((section) => ({
        section,
        sectionLabel: invoiceChangeSectionLabel(section),
        lines: grouped.get(section) ?? [],
    }));
}
function invoiceChangeLogSessionTitle(session) {
    if (session.action === "cancel")
        return "Invoice cancelled";
    if (session.action === "close")
        return "Invoice closed";
    return session.materialEditVersion != null
        ? `Edit v${session.materialEditVersion}`
        : "Invoice edited";
}
function toIsoCreatedAt(value) {
    if (value instanceof Date)
        return value.toISOString();
    return String(value);
}
function readEditSessionPayload(entry) {
    if (entry.changeType !== "edit_session")
        return null;
    const raw = entry.newValue;
    if (!raw || typeof raw !== "object")
        return null;
    const payload = raw;
    if (!Array.isArray(payload.changes))
        return null;
    return {
        action: payload.action ?? "edit",
        changes: payload.changes,
    };
}
function inferLegacySessionAction(changes) {
    if (changes.length === 1 && changes[0]?.field === "status") {
        if (changes[0].newDisplay === "Cancelled")
            return "cancel";
        if (changes[0].newDisplay === "Closed")
            return "close";
    }
    if (changes.every((c) => c.field === "status" && c.newDisplay === "Cancelled")) {
        return "cancel";
    }
    if (changes.every((c) => c.field === "status" && c.newDisplay === "Closed")) {
        return "close";
    }
    return "edit";
}
/**
 * Collapse changelog rows into edit sessions, then format material lines for
 * the client pay-page "What changed" panel (grouped by session, not a flat dump).
 */
function groupMaterialChangeSessionsForClient(entries, materialOnly = true) {
    const sessions = [];
    const legacyBuckets = new Map();
    for (const entry of entries) {
        const payload = readEditSessionPayload(entry);
        if (payload) {
            sessions.push({
                id: entry.id,
                materialEditVersion: entry.materialEditVersion ?? null,
                action: payload.action,
                createdAt: toIsoCreatedAt(entry.createdAt),
                changes: payload.changes,
            });
            continue;
        }
        if (entry.changeType === "edit_session")
            continue;
        const created = toIsoCreatedAt(entry.createdAt).slice(0, 19);
        const key = `${entry.actorUserId ?? ""}:${created}`;
        const bucket = legacyBuckets.get(key) ?? [];
        bucket.push(entry);
        legacyBuckets.set(key, bucket);
    }
    for (const [, bucket] of legacyBuckets) {
        if (!bucket.length)
            continue;
        const first = bucket[0];
        const changes = bucket.map((row) => ({
            section: row.section,
            changeType: row.changeType,
            field: row.field,
            label: row.label,
            oldDisplay: row.oldDisplay,
            newDisplay: row.newDisplay,
            isMaterial: row.isMaterial,
        }));
        sessions.push({
            id: first.id,
            materialEditVersion: first.materialEditVersion ?? null,
            action: inferLegacySessionAction(changes),
            createdAt: toIsoCreatedAt(first.createdAt),
            changes,
        });
    }
    sessions.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return sessions
        .map((session) => {
        const sections = groupInvoiceChangeLogLinesForClient(session.changes, materialOnly).map((group) => ({
            section: group.sectionLabel,
            sectionKey: group.section,
            lines: group.lines,
        }));
        return {
            id: session.id,
            title: invoiceChangeLogSessionTitle(session),
            materialEditVersion: session.materialEditVersion,
            action: session.action,
            createdAt: session.createdAt,
            sections,
        };
    })
        .filter((session) => session.sections.some((section) => section.lines.length > 0));
}
/** Multiline "WHAT CHANGED" block for emails / rich summaries. */
function formatWhatChangedSummary(groups) {
    if (!groups.length)
        return "Your invoice details were updated.";
    const parts = ["What changed", ""];
    for (const group of groups) {
        parts.push(group.sectionLabel);
        for (const line of group.lines) {
            parts.push(withInvoiceChangeBullet(line));
        }
        parts.push("");
    }
    while (parts.length && parts[parts.length - 1] === "")
        parts.pop();
    return parts.join("\n");
}
/** Short recurring schedule blurb for cancelled/closed pay pages (from live metadata, not changelog). */
function formatRecurringScheduleSummaryForClient(meta) {
    if (!meta)
        return null;
    if (meta.collectionMode && meta.collectionMode !== "recur")
        return null;
    const frequency = formatInvoiceChangeDisplayValue("recurringFrequency", meta.recurringFrequency);
    if (frequency === "—")
        return null;
    const endCondition = String(meta.recurringEndCondition ?? "").trim();
    if (endCondition === "count") {
        const n = Math.floor(Number(meta.recurringCount) || 0);
        if (n > 0) {
            return `This was a recurring invoice (${frequency.toLowerCase()}, ${n} time${n === 1 ? "" : "s"}).`;
        }
    }
    if (endCondition === "date" && meta.recurringEndDate) {
        return `This was a recurring invoice (${frequency.toLowerCase()}, until ${String(meta.recurringEndDate)}).`;
    }
    return `This was a recurring invoice (${frequency.toLowerCase()}).`;
}
function formatInvoiceTotalLine(oldDisplay, newDisplay) {
    const oldAmount = parseMoneyAmount(oldDisplay);
    const newAmount = parseMoneyAmount(newDisplay);
    if (oldAmount == null || newAmount == null) {
        return `Total invoice amount changed from ${oldDisplay} to ${newDisplay}.`;
    }
    const currency = parseMoneyCurrency(newDisplay) || parseMoneyCurrency(oldDisplay) || "SGD";
    const delta = Math.round((newAmount - oldAmount) * 100) / 100;
    const deltaAbs = formatMoneyNumber(Math.abs(delta), currency);
    if (delta > 0) {
        return `Total invoice amount increased from ${oldDisplay} to ${newDisplay} (+${deltaAbs})`;
    }
    if (delta < 0) {
        return `Total invoice amount decreased from ${oldDisplay} to ${newDisplay} (-${deltaAbs})`;
    }
    return `Total invoice amount unchanged at ${newDisplay}.`;
}
function normalizeLegacyLineItemPair(oldDisplay, newDisplay) {
    const oldRaw = String(oldDisplay ?? "").trim();
    const newRaw = String(newDisplay ?? "").trim();
    const oldLegacy = oldRaw.match(LEGACY_QTY_UNIT);
    const newLegacy = newRaw.match(LEGACY_QTY_UNIT);
    if (oldLegacy && newLegacy) {
        const oldName = oldLegacy[1].trim();
        const newName = newLegacy[1].trim();
        const oldTotal = Number(oldLegacy[2]) * Number(String(oldLegacy[3]).replace(/,/g, ""));
        const newTotal = Number(newLegacy[2]) * Number(String(newLegacy[3]).replace(/,/g, ""));
        if (Math.abs(oldTotal - newTotal) < 0.01) {
            return { oldDisplay: oldName, newDisplay: newName, label: newName, kind: "rename" };
        }
        const currency = "SGD";
        return {
            oldDisplay: formatMoneyNumber(oldTotal, currency),
            newDisplay: formatMoneyNumber(newTotal, currency),
            label: newName,
            kind: "amount",
        };
    }
    return { oldDisplay: oldRaw, newDisplay: newRaw };
}
function looksLikeMoney(value) {
    return MONEY_DISPLAY.test(value.trim()) || /^[A-Z]{3}\s+[\d,]+/.test(value.trim());
}
function parseMoneyCurrency(value) {
    const match = value.trim().match(/^([A-Z]{3})\s+/);
    return match?.[1] ?? null;
}
function parseMoneyAmount(value) {
    const match = value.trim().match(/(?:[A-Z]{3}\s+)?([\d,]+(?:\.\d+)?)/);
    if (!match)
        return null;
    const n = Number(String(match[1]).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
}
function formatMoneyNumber(amount, currency) {
    return `${currency} ${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function titleCaseFromKey(key) {
    return key
        .replace(/[_.]+/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
